import * as THREE from 'three';
import { createFloorPlan } from './blueprint';

/**
 * 3D 建筑生成器
 */

export interface BuildingConfig {
  wallHeight: number;
  wallThickness: number;
  floorThickness: number;
  ceilingHeight: number;
}

/**
 * 创建 3D 墙体
 */
export function create3DWalls(config: BuildingConfig): THREE.Group {
  const wallGroup = new THREE.Group();
  wallGroup.name = '3d-walls';

  const floorPlan = createFloorPlan();
  const wallHeight = config.wallHeight;
  const wallThickness = config.wallThickness;

  // 墙体材质
  const wallMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5f5f5,
    roughness: 0.9,
    metalness: 0.0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0
  });

  // 外墙材质
  const exteriorMaterial = new THREE.MeshStandardMaterial({
    color: 0xe8e8e8,
    roughness: 0.85,
    metalness: 0.0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0
  });

  // 收集所有墙体段
  const wallSegments: Array<{
    p1: THREE.Vector2;
    p2: THREE.Vector2;
    thickness: number;
    isExterior: boolean;
    room: string;
  }> = [];

  // 添加外墙
  const exteriorPoints = [
    { p1: new THREE.Vector2(-7.5, -5), p2: new THREE.Vector2(7.5, -5) },
    { p1: new THREE.Vector2(7.5, -5), p2: new THREE.Vector2(7.5, 5) },
    { p1: new THREE.Vector2(7.5, 5), p2: new THREE.Vector2(-7.5, 5) },
    { p1: new THREE.Vector2(-7.5, 5), p2: new THREE.Vector2(-7.5, -5) },
  ];

  exteriorPoints.forEach(({ p1, p2 }) => {
    wallSegments.push({
      p1,
      p2,
      thickness: 0.2,
      isExterior: true,
      room: 'exterior'
    });
  });

  // 添加内墙
  floorPlan.walls.filter(w => w.room !== 'exterior').forEach((wallData) => {
    if (wallData.points.length >= 2) {
      wallSegments.push({
        p1: wallData.points[0],
        p2: wallData.points[1],
        thickness: wallData.thickness || wallThickness,
        isExterior: false,
        room: wallData.room
      });
    }
  });

  // 创建所有墙体
  wallSegments.forEach((segment) => {
    const material = segment.isExterior ? exteriorMaterial : wallMaterial.clone();
    const mesh = createWallSegment(segment.p1, segment.p2, wallHeight, segment.thickness, material);
    mesh.userData.isExterior = segment.isExterior;
    mesh.userData.room = segment.room;
    wallGroup.add(mesh);
  });

  // 创建墙角连接
  const cornerPositions = createCornerPositions(wallSegments);
  cornerPositions.forEach((corner) => {
    const cornerMesh = createCornerPiece(corner.position, wallHeight, corner.thickness, exteriorMaterial.clone());
    cornerMesh.userData.isCorner = true;
    wallGroup.add(cornerMesh);
  });

  return wallGroup;
}

function createWallSegment(
  p1: THREE.Vector2,
  p2: THREE.Vector2,
  height: number,
  thickness: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  const geometry = new THREE.BoxGeometry(length, height, thickness);
  const mesh = new THREE.Mesh(geometry, material);

  mesh.position.set(
    (p1.x + p2.x) / 2,
    height / 2,
    (p1.y + p2.y) / 2
  );

  const angle = Math.atan2(dy, dx);
  mesh.rotation.y = -angle;

  return mesh;
}

function createCornerPositions(segments: Array<{
  p1: THREE.Vector2;
  p2: THREE.Vector2;
  thickness: number;
  isExterior: boolean;
}>): Array<{ position: THREE.Vector3; thickness: number }> {
  const corners: Array<{ position: THREE.Vector3; thickness: number }> = [];
  const cornerPoints: Map<string, { point: THREE.Vector2; segments: typeof segments; thickness: number }> = new Map();

  segments.forEach((segment) => {
    const key1 = `${segment.p1.x.toFixed(2)},${segment.p1.y.toFixed(2)}`;
    const key2 = `${segment.p2.x.toFixed(2)},${segment.p2.y.toFixed(2)}`;

    if (!cornerPoints.has(key1)) {
      cornerPoints.set(key1, { point: segment.p1, segments: [], thickness: segment.thickness });
    }
    cornerPoints.get(key1)!.segments.push(segment);
    cornerPoints.get(key1)!.thickness = Math.max(cornerPoints.get(key1)!.thickness, segment.thickness);

    if (!cornerPoints.has(key2)) {
      cornerPoints.set(key2, { point: segment.p2, segments: [], thickness: segment.thickness });
    }
    cornerPoints.get(key2)!.segments.push(segment);
    cornerPoints.get(key2)!.thickness = Math.max(cornerPoints.get(key2)!.thickness, segment.thickness);
  });

  cornerPoints.forEach(({ point, segments: connectedSegments }) => {
    if (connectedSegments.length >= 2) {
      corners.push({
        position: new THREE.Vector3(point.x, 0, point.y),
        thickness: 0.2
      });
    }
  });

  return corners;
}

function createCornerPiece(
  position: THREE.Vector3,
  height: number,
  thickness: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const cornerGeometry = new THREE.BoxGeometry(thickness * 1.5, height, thickness * 1.5);
  const cornerMesh = new THREE.Mesh(cornerGeometry, material);
  cornerMesh.position.set(position.x, height / 2, position.z);
  return cornerMesh;
}

/**
 * 创建地板
 */
export function create3DFloor(config: BuildingConfig): THREE.Group {
  const floorGroup = new THREE.Group();
  floorGroup.name = '3d-floor';

  const floorMaterial = new THREE.MeshStandardMaterial({
    color: 0xc4a67c,
    roughness: 0.7,
    metalness: 0.0,
    transparent: true,
    opacity: 0
  });

  const floorGeometry = new THREE.BoxGeometry(15, config.floorThickness, 10);
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.position.set(0, -config.floorThickness / 2, 0);
  floorGroup.add(floor);

  return floorGroup;
}

/**
 * 创建精细家具
 */
export function create3DFurniture(): THREE.Group {
  const furnitureGroup = new THREE.Group();
  furnitureGroup.name = '3d-furniture';

  const floorPlan = createFloorPlan();

  // 材质定义
  const materials = {
    sofa: new THREE.MeshStandardMaterial({ color: 0x4a4a5a, roughness: 0.8, metalness: 0.1, transparent: true, opacity: 0 }),
    sofaLeg: new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.3, metalness: 0.8, transparent: true, opacity: 0 }),
    bed: new THREE.MeshStandardMaterial({ color: 0xf5f0e8, roughness: 0.9, metalness: 0.0, transparent: true, opacity: 0 }),
    bedFrame: new THREE.MeshStandardMaterial({ color: 0x8b7355, roughness: 0.6, metalness: 0.1, transparent: true, opacity: 0 }),
    pillow: new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95, metalness: 0.0, transparent: true, opacity: 0 }),
    table: new THREE.MeshStandardMaterial({ color: 0x8b6914, roughness: 0.4, metalness: 0.2, transparent: true, opacity: 0 }),
    cabinet: new THREE.MeshStandardMaterial({ color: 0xd4c4a8, roughness: 0.5, metalness: 0.1, transparent: true, opacity: 0 }),
    white: new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.3, metalness: 0.1, transparent: true, opacity: 0 }),
    metal: new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.2, metalness: 0.9, transparent: true, opacity: 0 }),
    ceramic: new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.1, metalness: 0.3, transparent: true, opacity: 0 }),
    glass: new THREE.MeshStandardMaterial({ color: 0xaaccff, roughness: 0.0, metalness: 0.1, transparent: true, opacity: 0.3 }),
    rug: new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 1.0, metalness: 0.0, transparent: true, opacity: 0 }),
    black: new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.5, metalness: 0.2, transparent: true, opacity: 0 }),
  };

  floorPlan.rooms.forEach((room) => {
    room.furniture.forEach((item) => {
      const bounds = getBounds(item.points);
      const centerX = (bounds.min.x + bounds.max.x) / 2;
      const centerZ = (bounds.min.y + bounds.max.y) / 2;
      const width = bounds.max.x - bounds.min.x;
      const depth = bounds.max.y - bounds.min.y;

      let meshes: THREE.Mesh[] = [];

      switch (item.type) {
        case 'sofa':
          meshes = createDetailedSofa(centerX, centerZ, width, depth, materials);
          break;
        case 'bed':
          meshes = createDetailedBed(centerX, centerZ, width, depth, materials);
          break;
        case 'table':
        case 'desk':
          meshes = createDetailedTable(centerX, centerZ, width, depth, materials);
          break;
        case 'tv':
          meshes = createTVStand(centerX, centerZ, width, depth, materials);
          break;
        case 'cabinet':
          meshes = createCabinet(centerX, centerZ, width, depth, materials);
          break;
        case 'wardrobe':
          meshes = createWardrobe(centerX, centerZ, width, depth, materials);
          break;
        case 'toilet':
          meshes = createToilet(centerX, centerZ, materials);
          break;
        case 'sink':
          meshes = createSink(centerX, centerZ, materials);
          break;
        case 'shower':
          meshes = createShower(centerX, centerZ, width, depth, materials);
          break;
        case 'stove':
          meshes = createStove(centerX, centerZ, width, depth, materials);
          break;
        case 'fridge':
          meshes = createFridge(centerX, centerZ, width, depth, materials);
          break;
        case 'rug':
          meshes = createRug(centerX, centerZ, width, depth, materials);
          break;
        case 'plant':
          meshes = createPlant(centerX, centerZ, materials);
          break;
      }

      meshes.forEach(mesh => {
        mesh.userData.furnitureId = item.id;
        mesh.userData.room = room.id;
        mesh.userData.furnitureType = item.type;
        furnitureGroup.add(mesh);
      });
    });
  });

  return furnitureGroup;
}

function createDetailedSofa(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const seatHeight = 0.4;
  const backHeight = 0.8;
  const armWidth = 0.15;
  const legHeight = 0.1;

  // 底座
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(w, seatHeight - legHeight, d),
    mat.sofa.clone()
  );
  base.position.set(cx, legHeight + (seatHeight - legHeight) / 2, cz);
  meshes.push(base);

  // 靠背
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(w, backHeight - seatHeight, d * 0.3),
    mat.sofa.clone()
  );
  back.position.set(cx, legHeight + seatHeight + (backHeight - seatHeight) / 2, cz - d * 0.35);
  meshes.push(back);

  // 左侧扶手
  const leftArm = new THREE.Mesh(
    new THREE.BoxGeometry(armWidth, backHeight - legHeight, d),
    mat.sofa.clone()
  );
  leftArm.position.set(cx - w / 2 + armWidth / 2, legHeight + (backHeight - legHeight) / 2, cz);
  meshes.push(leftArm);

  // 右侧扶手
  const rightArm = new THREE.Mesh(
    new THREE.BoxGeometry(armWidth, backHeight - legHeight, d),
    mat.sofa.clone()
  );
  rightArm.position.set(cx + w / 2 - armWidth / 2, legHeight + (backHeight - legHeight) / 2, cz);
  meshes.push(rightArm);

  // 座垫
  const cushion = new THREE.Mesh(
    new THREE.BoxGeometry(w - armWidth * 2 - 0.1, 0.15, d - 0.1),
    mat.sofa.clone()
  );
  cushion.position.set(cx, legHeight + seatHeight - 0.05, cz);
  meshes.push(cushion);

  // 腿
  const legPositions = [
    [cx - w / 2 + 0.1, cz - d / 2 + 0.1],
    [cx + w / 2 - 0.1, cz - d / 2 + 0.1],
    [cx - w / 2 + 0.1, cz + d / 2 - 0.1],
    [cx + w / 2 - 0.1, cz + d / 2 - 0.1],
  ];
  legPositions.forEach(([lx, lz]) => {
    const leg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, legHeight, 8),
      mat.sofaLeg.clone()
    );
    leg.position.set(lx, legHeight / 2, lz);
    meshes.push(leg);
  });

  return meshes;
}

function createDetailedBed(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const bedHeight = 0.5;
  const mattressHeight = 0.2;
  const headboardHeight = 1.0;
  const headboardThickness = 0.1;

  // 床框
  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(w, bedHeight - mattressHeight, d),
    mat.bedFrame.clone()
  );
  frame.position.set(cx, (bedHeight - mattressHeight) / 2, cz);
  meshes.push(frame);

  // 床垫
  const mattress = new THREE.Mesh(
    new THREE.BoxGeometry(w - 0.05, mattressHeight, d - 0.05),
    mat.bed.clone()
  );
  mattress.position.set(cx, bedHeight - mattressHeight / 2, cz);
  meshes.push(mattress);

  // 床头板
  const headboard = new THREE.Mesh(
    new THREE.BoxGeometry(w, headboardHeight - bedHeight, headboardThickness),
    mat.bedFrame.clone()
  );
  headboard.position.set(cx, bedHeight + (headboardHeight - bedHeight) / 2, cz - d / 2 - headboardThickness / 2);
  meshes.push(headboard);

  // 枕头 x2
  const pillowW = w / 2 - 0.15;
  const pillowD = d * 0.35;
  [-1, 1].forEach(side => {
    const pillow = new THREE.Mesh(
      new THREE.BoxGeometry(pillowW, 0.15, pillowD),
      mat.pillow.clone()
    );
    pillow.position.set(cx + side * (pillowW / 2 + 0.05), bedHeight + 0.15 / 2, cz - d / 2 + 0.15 + pillowD / 2);
    meshes.push(pillow);
  });

  // 被子
  const blanket = new THREE.Mesh(
    new THREE.BoxGeometry(w - 0.1, 0.1, d * 0.5),
    mat.bed.clone()
  );
  blanket.position.set(cx, bedHeight + 0.1 / 2, cz + d * 0.1);
  meshes.push(blanket);

  return meshes;
}

function createDetailedTable(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const height = 0.75;
  const thickness = 0.05;

  // 桌面
  const top = new THREE.Mesh(
    new THREE.BoxGeometry(w, thickness, d),
    mat.table.clone()
  );
  top.position.set(cx, height - thickness / 2, cz);
  meshes.push(top);

  // 桌腿
  const legPositions = [
    [cx - w / 2 + 0.05, cz - d / 2 + 0.05],
    [cx + w / 2 - 0.05, cz - d / 2 + 0.05],
    [cx - w / 2 + 0.05, cz + d / 2 - 0.05],
    [cx + w / 2 - 0.05, cz + d / 2 - 0.05],
  ];
  legPositions.forEach(([lx, lz]) => {
    const leg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.025, 0.03, height - thickness, 8),
      mat.table.clone()
    );
    leg.position.set(lx, (height - thickness) / 2, lz);
    meshes.push(leg);
  });

  return meshes;
}

function createTVStand(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 电视柜
  const stand = new THREE.Mesh(
    new THREE.BoxGeometry(w, 0.5, d),
    mat.cabinet.clone()
  );
  stand.position.set(cx, 0.25, cz);
  meshes.push(stand);

  // 电视
  const tv = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.8, w * 0.8 * 0.6, 0.05),
    mat.cabinet.clone()
  );
  tv.position.set(cx, 0.5 + (w * 0.8 * 0.6) / 2 + 0.05, cz);
  meshes.push(tv);

  // 电视屏幕（黑色）
  const screen = new THREE.Mesh(
    new THREE.BoxGeometry(w * 0.75, w * 0.8 * 0.55, 0.01),
    new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.1, metalness: 0.0 })
  );
  screen.position.set(cx, 0.5 + (w * 0.8 * 0.6) / 2 + 0.05, cz + 0.03);
  meshes.push(screen);

  return meshes;
}

function createCabinet(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const height = 0.8;

  // 柜体
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w, height, d),
    mat.cabinet.clone()
  );
  body.position.set(cx, height / 2, cz);
  meshes.push(body);

  // 门把手
  const handle = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.08, 0.02),
    mat.metal.clone()
  );
  handle.position.set(cx, height / 2, cz + d / 2 + 0.01);
  meshes.push(handle);

  return meshes;
}

function createWardrobe(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const height = 2.2;

  // 柜体
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w, height, d),
    mat.cabinet.clone()
  );
  body.position.set(cx, height / 2, cz);
  meshes.push(body);

  // 门缝
  const divider = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, height - 0.02, 0.01),
    mat.metal.clone()
  );
  divider.position.set(cx, height / 2, cz + d / 2 + 0.01);
  meshes.push(divider);

  return meshes;
}

function createToilet(cx: number, cz: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 底座
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.22, 0.4, 16),
    mat.white.clone()
  );
  base.position.set(cx, 0.2, cz);
  meshes.push(base);

  // 水箱
  const tank = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.4, 0.2),
    mat.white.clone()
  );
  tank.position.set(cx, 0.4 + 0.2, cz - 0.15);
  meshes.push(tank);

  // 盖子
  const lid = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.18, 0.02, 16),
    mat.white.clone()
  );
  lid.position.set(cx, 0.41, cz);
  meshes.push(lid);

  return meshes;
}

function createSink(cx: number, cz: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 水槽
  const basin = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.18, 0.15, 16),
    mat.ceramic.clone()
  );
  basin.position.set(cx, 0.85, cz);
  meshes.push(basin);

  // 水龙头
  const faucet = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.2, 8),
    mat.metal.clone()
  );
  faucet.position.set(cx, 0.85 + 0.1 + 0.1, cz - 0.15);
  meshes.push(faucet);

  // 水龙头出水口
  const spout = new THREE.Mesh(
    new THREE.CylinderGeometry(0.015, 0.015, 0.1, 8),
    mat.metal.clone()
  );
  spout.rotation.x = Math.PI / 4;
  spout.position.set(cx, 0.85 + 0.15, cz - 0.1);
  meshes.push(spout);

  return meshes;
}

function createShower(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 底座
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(w, 0.02, d),
    mat.ceramic.clone()
  );
  base.position.set(cx, 0.01, cz);
  meshes.push(base);

  // 花洒
  const head = new THREE.Mesh(
    new THREE.CylinderGeometry(0.15, 0.12, 0.05, 16),
    mat.metal.clone()
  );
  head.position.set(cx, 1.8, cz);
  meshes.push(head);

  // 花洒杆
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 1.5, 8),
    mat.metal.clone()
  );
  pole.position.set(cx, 1.0, cz);
  meshes.push(pole);

  return meshes;
}

function createStove(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 灶台
  const top = new THREE.Mesh(
    new THREE.BoxGeometry(w, 0.05, d),
    mat.black.clone ? mat.black.clone() : new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.5 })
  );
  top.position.set(cx, 0.9, cz);
  meshes.push(top);

  // 灶眼 x4
  const burnerPositions = [[-0.15, -0.1], [0.15, -0.1], [-0.15, 0.1], [0.15, 0.1]];
  burnerPositions.forEach(([bx, bz]) => {
    const burner = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, 0.02, 16),
      mat.metal.clone()
    );
    burner.position.set(cx + bx * w, 0.9 + 0.03, cz + bz * d);
    meshes.push(burner);
  });

  return meshes;
}

function createFridge(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 冷藏室
  const fridge = new THREE.Mesh(
    new THREE.BoxGeometry(w, 1.1, d),
    mat.white.clone()
  );
  fridge.position.set(cx, 0.55, cz);
  meshes.push(fridge);

  // 冷冻室
  const freezer = new THREE.Mesh(
    new THREE.BoxGeometry(w, 0.6, d),
    mat.white.clone()
  );
  freezer.position.set(cx, 1.1 + 0.3, cz);
  meshes.push(freezer);

  // 门把手
  const handle = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 0.2, 0.03),
    mat.metal.clone()
  );
  handle.position.set(cx + w / 2 + 0.02, 0.8, cz + d / 2 + 0.01);
  meshes.push(handle);

  return meshes;
}

function createRug(cx: number, cz: number, w: number, d: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  const rug = new THREE.Mesh(
    new THREE.BoxGeometry(w, 0.02, d),
    mat.rug.clone()
  );
  rug.position.set(cx, 0.01, cz);
  meshes.push(rug);

  return meshes;
}

function createPlant(cx: number, cz: number, mat: any): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];

  // 花盆
  const pot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.1, 0.25, 12),
    mat.ceramic.clone()
  );
  pot.position.set(cx, 0.125, cz);
  meshes.push(pot);

  // 植物（简化为几个球体）
  const leaves = new THREE.Mesh(
    new THREE.SphereGeometry(0.2, 8, 6),
    new THREE.MeshStandardMaterial({ color: 0x228b22, roughness: 0.8 })
  );
  leaves.position.set(cx, 0.4, cz);
  meshes.push(leaves);

  return meshes;
}

function getBounds(points: THREE.Vector2[]): { min: THREE.Vector2; max: THREE.Vector2 } {
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  points.forEach(p => {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  });

  return {
    min: new THREE.Vector2(minX, minY),
    max: new THREE.Vector2(maxX, maxY)
  };
}

export type { THREE };
